/* eslint-disable tss-unused-classes/unused-classes */
import { emphasize } from '@mui/material';
import { DataVisualizationColor } from '@mui/material/styles/createPalette';
import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  GraphVizIconShape,
  GraphVizTileEmphasis,
} from '@/modules/graphViz/graphVizNodeConfig';
import {
  generateDataVisualizationPaletteFromBaseColor,
  getContrastTextForColor,
} from '@/styles/themes/common';
import { COLORS } from '@/styles/tokens/colors';
import { WithClasses } from '@/styles/types';
import { isPrintPath } from '@/utils/isPrintPath';

import {
  TILE_DEFAULT_FONT_SIZE_VARIANT,
  TILE_DEFAULT_WIDTH,
} from './constants';
import {
  getIconBackgroundShadeForEmphasis,
  getTileBodyShadeForEmphasis,
  getTileStripeShadeForEmphasis,
  SemanticTileKind,
  tileWidthScaleForVariant,
} from './Tile.utils';
import { TileFontSizeVariant, TileVariant } from './types';

interface VariantProps {
  variant: TileVariant;
  dragging: boolean;
  hover: boolean;
  viewOnly: boolean;
  isIconVariant: boolean;
  hasFixedWidth: boolean;
  isSelected: boolean;
  fontSizeVariant: TileFontSizeVariant;
  kind?: SemanticTileKind;
  iconShape?: GraphVizIconShape;
  tileEmphasis?: GraphVizTileEmphasis;
  dimensionsFinalized?: boolean;
}

// Icon sizing baselines at the SM variant. Per-variant values are derived by
// multiplying by tileWidthScaleForVariant (cumulative 1.25^N from SM).
const SHAPED_ICON_CONTAINER_BASE = 56;
const SHAPED_ICON_INNER_BASE = 24;
const LEGACY_ICON_SIZE_BASE = 20;
const LEGACY_ICON_PADDING_BASE = 10;
// Icon tiles have no default width, so unbounded notes could stretch them arbitrarily wide.
const ICON_TILE_NOTES_MAX_WIDTH = TILE_DEFAULT_WIDTH;

function getShapedIconStyles(iconShape: GraphVizIconShape, scale: number) {
  const containerSize = SHAPED_ICON_CONTAINER_BASE * scale;
  const base = {
    width: containerSize,
    height: containerSize,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  };

  switch (iconShape) {
    case GraphVizIconShape.Square:
      return {
        ...base,
        clipPath: `inset(${6 * scale}px)`,
      };
    case GraphVizIconShape.Rounded:
      return {
        ...base,
        clipPath: `inset(${6 * scale}px round ${12 * scale}px)`,
      };
    case GraphVizIconShape.Triangle:
      return {
        ...base,
        clipPath: `polygon(50% ${4 * scale}px, 0% ${52 * scale}px, 100% ${52 * scale}px)`,
        paddingTop: 24 * scale,
        paddingBottom: 8 * scale,
      };
    case GraphVizIconShape.Diamond:
      return {
        ...base,
        clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
      };
    case GraphVizIconShape.Circle:
      return {
        ...base,
        borderRadius: '50%',
        clipPath: `circle(${22 * scale}px at 50% 50%)`,
      };
  }
}

export const useStyles = makeStyles<VariantProps>()((
  theme,
  {
    dragging,
    viewOnly,
    isIconVariant,
    hasFixedWidth,
    variant,
    kind,
    iconShape,
    fontSizeVariant,
    dimensionsFinalized,
  }
) => {
  const viewOnlyStyles = viewOnly
    ? {
        '@media screen': {
          boxShadow: 'none',
        },
      }
    : {};

  const hoverStyles = {};
  const baseBoxShadow = isIconVariant ? undefined : theme.palette.shadows.md;
  const isExternal = variant === TileVariant.External;
  const isHidden = kind === SemanticTileKind.Hidden;

  // Legacy (no iconShape) tiles render as a single rounded pill. Shape-enabled
  // tiles use a square bounding box with clip-path for non-rectangular shapes.
  const iconScale = tileWidthScaleForVariant(fontSizeVariant);
  const legacyIconShapeStyles = {
    borderRadius: '50%',
    padding: LEGACY_ICON_PADDING_BASE * iconScale,
  };
  const shapedIconExtraStyles = iconShape
    ? getShapedIconStyles(iconShape, iconScale)
    : legacyIconShapeStyles;

  const iconInnerSize = iconShape
    ? SHAPED_ICON_INNER_BASE * iconScale
    : LEGACY_ICON_SIZE_BASE * iconScale;

  return {
    wrapper: {
      // An inline flex box is indivisible during PDF pagination. A block flex
      // box can split at its unscaled height and clip a scaled diagram tile.
      // Use the print route, not print media: the PDF service uses screen media.
      // This also changes browser layout on that route, but not in the editor.
      display: isIconVariant || isPrintPath() ? 'inline-flex' : 'flex',
      // Avoid baseline spacing below the inline box. This can affect line-box
      // layout; inline-flex above, not this alignment, fixes PDF clipping.
      ...(isPrintPath() && { verticalAlign: 'top' }),
      height: '100%',
      position: 'relative',
    },
    root: {
      display: 'flex',
      flexDirection: 'column',
      '@media print': {
        boxShadow: 'none',
      },
      ...viewOnlyStyles,
      width: '100%',
      borderRadius: '4px',
      outlineOffset: -1,
      boxShadow: dragging ? theme.palette.shadows.lg : baseBoxShadow,
      outlineWidth: '1px',
      ...(isExternal && {
        outlineStyle: 'dashed',
      }),
      ...(isIconVariant && {
        outlineStyle: 'transparent',
        // Center the icon + label within the box, matching edge-label nodes
        // which stay centered while resizing. `safe` falls back to start
        // alignment when content is taller than the box, so legacy
        // pre-icon-scaling tiles (stored height < content) overflow downward
        // rather than shifting up into the node above.
        justifyContent: 'safe center',
        // Until a user has intentionally sized the tile (dimensionsFinalized),
        // let icon tiles overflow so pre-icon-scaling diagrams stay readable.
        ...(!dimensionsFinalized && { overflow: 'visible' }),
      }),
      ...(!(isIconVariant || isExternal) && {
        outlineStyle: 'solid',
        borderLeftWidth: '6px',
        borderLeftStyle: 'solid',
      }),
      '&:hover': hoverStyles,
    },
    innerRoot: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: isIconVariant ? 'column' : 'row',
      gap: isIconVariant ? 8 : 4,
      width: '100%',
      padding: isIconVariant ? 8 : '12px 16px',
      '~ *': {
        borderTopWidth: '1px',
        borderTopStyle: isExternal ? 'dashed' : 'solid',
      },
    },
    innerRootSelected: {},
    footerWrapper: { marginTop: 'auto' },
    footerWrapperSelected: {},
    hoverRoot: hoverStyles,
    selectedRoot: hoverStyles,
    linesWrapper: {
      ...(isIconVariant && {
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
        ...(hasFixedWidth && { width: '100%' }),
      }),
    },
    lineOne: {
      marginBottom: 4,
      ...(isIconVariant && { flexShrink: 0 }),
    },
    lineTwo: isIconVariant ? { flexShrink: 0 } : {},
    lineThree: isIconVariant ? { flexShrink: 0 } : {},
    notes: {
      paddingTop: theme.spacing(1.5),
      ...(isIconVariant && {
        maxWidth: hasFixedWidth ? '100%' : ICON_TILE_NOTES_MAX_WIDTH,
        width: '100%',
        overflowWrap: 'break-word',
        flexShrink: 0,
      }),
      // Let clicks pass through to the parent tile for ReactFlow onNodeClick.
      pointerEvents: 'none',
      '#text-editor-content-editable': {
        // Override editor default (150px) so the tile can size to content.
        minHeight: 'auto !important',
        padding: '0 !important',
        ...(isIconVariant && {
          textAlign: 'left',
          '& > p': {
            margin: `0 ${theme.spacing(3)}`,
            textAlign: 'left',
          },
          '& ul, & ol': {
            listStylePosition: 'outside',
            margin: 0,
            maxWidth: '100%',
            padding: 0,
            width: '100%',
            textAlign: 'left',
          },
          '& li': {
            margin: `0 ${theme.spacing(3)}`,
            paddingLeft: 0,
            textAlign: 'left',
            '&::marker': {
              textShadow: 'none',
            },
          },
          '& mark': {
            textShadow: 'none',
          },
        }),
      },
    },
    // Non-clipping positioning context so the warning indicator can sit
    // outside the icon's clip-path. Only rendered when an iconShape is set
    // (see TileCard).
    iconWrapper: {
      position: 'relative' as const,
      display: 'inline-flex',
    },
    icon: {
      '*, > span': {
        width: iconInnerSize,
        height: iconInnerSize,
      },
      color: isHidden ? COLORS.GRAY[300] : COLORS.PRIMITIVES.WHITE,
      backgroundColor: isHidden ? COLORS.PRIMITIVES.WHITE : undefined,
      border: isHidden ? `1px solid ${COLORS.GRAY[200]}` : undefined,
      ...shapedIconExtraStyles,
      display: 'flex',
      position: 'relative',
    },
    warningOverflow: {
      overflow: 'visible',
    },
    warningIndicator: {
      position: 'absolute',
      top: 0,
      right: 0,
      transform: 'translate(50%, -50%)',
      zIndex: 1,
    },
  };
});

function makeTileVariant(
  colorMap: Pick<
    DataVisualizationColor,
    'main' | 'dark' | 'ultraLight' | 'lighter' | 'light'
  >,
  { isIconVariant, tileEmphasis }: VariantProps
) {
  // Body / stripe emphasis only applies to standard tiles. Icon tiles keep
  // the transparent body and default stripe; their emphasis is driven by
  // `iconBackgroundColor` below.
  const emphasisForBody = isIconVariant ? undefined : tileEmphasis;
  const tileBodyBackgroundColor = getTileBodyShadeForEmphasis(
    colorMap,
    emphasisForBody
  );
  // Hover is a subtle, hue-preserving shift of the resting body. `emphasize`
  // lightens dark bodies and darkens light ones by a small amount, so it reads
  // as the same color a touch stronger — no oversaturation (as `darken` causes
  // on pale bodies) and no muddiness (as blending toward gray causes on
  // saturated ones). It stays consistent across emphasis levels and tenant
  // colors, and is a hair lighter than the selected shade (0.1).
  const tileBodyHoverBackgroundColor = emphasize(tileBodyBackgroundColor, 0.06);
  const borderColor = getTileStripeShadeForEmphasis(colorMap, emphasisForBody);
  // For icon tiles, emphasis darkens the icon's background shape. Low (and
  // legacy tiles with no emphasis set) keeps the existing `main` shade.
  const iconBackgroundColor = getIconBackgroundShadeForEmphasis(
    colorMap,
    tileEmphasis
  );
  const borderColorSelected = colorMap.dark;

  const iconHoverStyles = {
    '& .Tile-Icon': {
      color: getContrastTextForColor(iconBackgroundColor),
    },
  };

  const iconSelectedStyles = {
    outlineStyle: 'solid',
  };

  const hoverStyles = {
    outlineColor: borderColorSelected,
    borderColor: borderColorSelected,
    backgroundColor: tileBodyHoverBackgroundColor,
    ...(isIconVariant && {
      ...iconHoverStyles,
      outlineStyle: 'solid',
    }),
  };

  return {
    wrapper: {},
    root: {
      backgroundColor: isIconVariant ? 'transparent' : tileBodyBackgroundColor,
      borderColor,
      outlineColor: borderColor,
      '&:hover': hoverStyles,
    },
    innerRoot: {
      '~ *': { borderColor },
    },
    innerRootSelected: {
      '~ *': {
        // @emotion weirdly applying styles out of order again
        borderColor: `${borderColorSelected} !important`,
      },
    },
    footerWrapper: {
      backgroundColor: colorMap.lighter,
    },
    footerWrapperSelected: {
      backgroundColor: colorMap.light,
    },
    hoverRoot: hoverStyles,
    selectedRoot: {
      outlineColor: borderColorSelected,
      borderColor: borderColorSelected,
      backgroundColor: tileBodyHoverBackgroundColor,
      ...(isIconVariant && { ...iconHoverStyles, ...iconSelectedStyles }),
    },
    linesWrapper: {},
    lineOne: { marginBottom: 4 },
    lineTwo: {},
    lineThree: {},
    notes: {},
    iconWrapper: {},
    icon: {
      background: iconBackgroundColor,
    },
    warningOverflow: {},
    warningIndicator: {},
  };
}

export const usePrimaryStyles = makeStyles<VariantProps>()((theme, props) => {
  return makeTileVariant(theme.palette.dataVisualizationPrimary, props);
});

export const useSecondaryStyles = makeStyles<VariantProps>()((theme, props) => {
  return makeTileVariant(theme.palette.dataVisualizationSecondary, props);
});

export const useTertiaryStyles = makeStyles<VariantProps>()((theme, props) => {
  return makeTileVariant(theme.palette.dataVisualizationTertiary, props);
});

export const useDestructiveStyles = makeStyles<VariantProps>()((
  theme,
  props
) => {
  return makeTileVariant(theme.palette.dataVisualizationNegative, props);
});

export const useExternalStyles = makeStyles<VariantProps>()((_theme, props) => {
  const baseStyles = makeTileVariant(
    {
      ultraLight: COLORS.GRAY[50],
      lighter: COLORS.GRAY[100],
      light: COLORS.GRAY[200],
      main: COLORS.GRAY[400],
      dark: COLORS.GRAY[800],
    },
    props
  );

  return {
    ...baseStyles,
    root: {
      ...baseStyles.root,
      backgroundColor: COLORS.PRIMITIVES.WHITE,
    },
  };
});

export const useDefaultStyles = makeStyles<VariantProps>()((_theme, props) => {
  return makeTileVariant(
    {
      ultraLight: COLORS.GRAY[50],
      lighter: COLORS.GRAY[100],
      light: COLORS.GRAY[200],
      main: COLORS.GRAY[300],
      dark: COLORS.GRAY[800],
    },
    props
  );
});

/** Colors a tile with the shades of a hex color in place of a palette. */
const useCustomColorStyles = makeStyles<
  VariantProps & { customColor: string }
>()((_theme, { customColor, ...props }) =>
  makeTileVariant(
    generateDataVisualizationPaletteFromBaseColor(customColor),
    props
  )
);

export function useTileClasses(props: {
  variant: TileVariant;
  customColor?: string;
  viewOnly: boolean;
  dragging?: boolean;
  classes?: WithClasses<typeof useStyles>;
  hover?: boolean;
  isIconVariant?: boolean;
  hasFixedWidth?: boolean;
  isSelected?: boolean;
  fontSizeVariant?: TileFontSizeVariant;
  kind?: SemanticTileKind;
  iconShape?: GraphVizIconShape;
  tileEmphasis?: GraphVizTileEmphasis;
  dimensionsFinalized?: boolean;
}) {
  const variantProps = useMemo(() => {
    return {
      variant: props.variant,
      dragging: props.dragging ?? false,
      hover: props.hover ?? false,
      viewOnly: props.viewOnly,
      isIconVariant: props.isIconVariant ?? false,
      hasFixedWidth: props.hasFixedWidth ?? false,
      isSelected: props.isSelected ?? false,
      fontSizeVariant: props.fontSizeVariant ?? TILE_DEFAULT_FONT_SIZE_VARIANT,
      kind: props.kind,
      iconShape: props.iconShape,
      tileEmphasis: props.tileEmphasis,
      dimensionsFinalized: props.dimensionsFinalized,
    };
  }, [
    props.dragging,
    props.hover,
    props.isIconVariant,
    props.hasFixedWidth,
    props.isSelected,
    props.variant,
    props.viewOnly,
    props.fontSizeVariant,
    props.kind,
    props.iconShape,
    props.tileEmphasis,
    props.dimensionsFinalized,
  ]);

  const { classes } = useStyles(variantProps, { props });

  const { classes: primaryClasses } = usePrimaryStyles(variantProps, { props });
  const { classes: secondaryClasses } = useSecondaryStyles(variantProps, {
    props,
  });
  const { classes: tertiaryClasses } = useTertiaryStyles(variantProps, {
    props,
  });
  const { classes: defaultClasses } = useDefaultStyles(variantProps, { props });
  const { classes: destructiveClasses } = useDestructiveStyles(variantProps, {
    props,
  });
  const { classes: externalClasses } = useExternalStyles(variantProps, {
    props,
  });
  // Hooks cannot be conditional. Without a custom color, the gray fallback
  // classes are not used.
  const { classes: customColorClasses } = useCustomColorStyles(
    { ...variantProps, customColor: props.customColor ?? COLORS.GRAY[300] },
    { props }
  );

  const variantClasses: Record<
    Exclude<TileVariant, typeof TileVariant.Pixel>,
    typeof classes
  > = {
    primary: primaryClasses,
    secondary: secondaryClasses,
    tertiary: tertiaryClasses,
    destructive: destructiveClasses,
    default: defaultClasses,
    external: externalClasses,
  };
  // A custom color replaces only the colors of the variant. The variant still
  // selects the default icon and the external outline.
  if (props.customColor && props.variant !== TileVariant.Pixel) {
    variantClasses[props.variant] = customColorClasses;
  }

  return {
    classes,
    variantClasses,
    primaryClasses,
    secondaryClasses,
    tertiaryClasses,
    destructiveClasses,
    defaultClasses,
  };
}
